// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { Link, Navigate, useParams } from "react-router-dom"
import { useEvolutionCycle } from "@/queries/queries.evolutionCycles"
import { useCycleEvents } from "@/queries/queries.eventJournal"
import { isTerminal } from "@/lib/refresh-policy"
import { lastEventAgeMs } from "@/lib/live-signal"
import { useCompletionNotification } from "@/hooks/useCompletionNotification"
import type { RawFeedEvent } from "@/lib/event-feed-curation"
import { FullPageSpinnerWithText } from "@/components/ui/spinner"
import CycleDashboard from "./dashboard"
import SetupPanel from "./dashboard/SetupPanel"
import { CycleStatusBadge } from "./cycleStatusBadge"
import { formatElapsed } from "./cycleDuration"
import { selectCycleConfig } from "@/lib/cycle-config"

// SetupPanel reads the launch-time snapshot for immutability (INV-8, ADR-0007)
// but only through the dataset-free evaluation-protocol selector (INV-7).
const EvolutionCycleDetailsView = () => {
  const { cycleId } = useParams<{ cycleId: string }>()

  if (!cycleId) {
    return <Navigate to="/forges" replace />
  }

  return <EvolutionCycleDetailsContent cycleId={cycleId} />
}

const EvolutionCycleDetailsContent = ({ cycleId }: { cycleId: string }) => {
  const { data, isLoading, isError } = useEvolutionCycle(cycleId)

  if (isLoading) {
    return <FullPageSpinnerWithText text="Loading evolution cycle" />
  }
  if (isError || !data) {
    return (
      <div
        role="alert"
        className="m-4 rounded-md border border-failed/40 bg-failed/10 p-4 text-failed-fg"
      >
        Could not load evolution cycle {cycleId}.
      </div>
    )
  }

  const engine = selectCycleConfig(data)?.engine ?? null

  return (
    <div className="flex flex-col gap-4 px-6 pb-16 pt-4">
      {/* title + status + liveness (mockup "2A" header band) */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[25px] font-semibold tracking-[-0.015em] text-foreground">
              <Link
                to={`/forges/${data.forge_id}`}
                className="transition-all hover:[text-shadow:0_0_16px_rgba(69,240,200,0.35)]"
              >
                {data.forge_name}
              </Link>
            </h1>
            <CycleStatusBadge status={data.status} />
            {engine ? <EngineBadge engine={engine} /> : null}
          </div>
          <span className="select-text font-mono text-xs text-text-tertiary">
            {data.cycle_id} · owner {data.owner_user_id} · started{" "}
            {new Date(data.started_at).toLocaleDateString()}
          </span>
        </div>
        <LiveIndicator cycleId={cycleId} status={data.status} />
      </div>

      {/* SETUP — collapsible structured "frozen at launch" region
          (issue `fe-setup-panel`) replacing the raw forge-spec JSON
          dump previously rendered here. */}
      <SetupPanel cycleId={cycleId} />

      <CycleDashboard cycleId={cycleId} />
    </div>
  )
}

// No polling of its own (INV-3): useCycleEvents is kept fresh by refresh-policy.
// Carries the `Live status` landmark — distinct from the `Cycle status` landmark on CycleStatusBadge.
const LiveIndicator = ({
  cycleId,
  status,
}: {
  cycleId: string
  status: CycleStatus
}) => {
  const eventsQuery = useCycleEvents({ cycleId, mode: "relevant" })
  const terminal = isTerminal(status)
  const rawEvents: RawFeedEvent[] = eventsQuery.data?.items ?? []
  const ageMs = lastEventAgeMs(rawEvents, Date.now())

  // Re-homed completion notification (option B) — fires invisibly on a
  // running -> terminal transition; no ticker, no enable button.
  useCompletionNotification(cycleId, status)

  return (
    <span
      role="status"
      aria-label="Live status"
      data-terminal={terminal}
      data-pulsing={!terminal}
      className="inline-flex items-center gap-2 whitespace-nowrap text-[12.5px] text-[#A9CFC6]"
    >
      <span
        aria-hidden
        className={`size-1.5 rounded-full ${
          terminal
            ? "bg-text-tertiary"
            : "bg-primary shadow-[0_0_8px_var(--primary)] animate-pulse-dot"
        }`}
      />
      {terminal
        ? "Finished"
        : ageMs != null
          ? `Live — ${formatElapsed(ageMs)} ago`
          : "Live"}
    </span>
  )
}

const EngineBadge = ({ engine }: { engine: string }) => (
  <span
    aria-label={`Evolver engine: ${engine}`}
    data-engine={engine}
    className="inline-flex w-fit items-center rounded-full border border-border-strong bg-surface-strong px-2.5 py-0.5 text-[11.5px] font-medium capitalize text-text-secondary"
  >
    {engine}
  </span>
)

export default EvolutionCycleDetailsView
